import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '@/store';

/**
 * What the browser will move focus to with Tab. `:not([disabled])` matters
 * more than it looks: the first control in a form is often disabled until
 * something is filled in, and focusing a disabled element silently leaves
 * focus on <body> — outside the dialog, which is the one place it must not
 * be.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Innermost open overlay wins.
 *
 * Every overlay listens on `document`, so with two of them open — the
 * driver-key modal over the driver list, say — both would see the same Tab
 * and both would drag focus into their own panel. The one opened last is
 * the one the dispatcher is looking at, so it is the only one that traps.
 * Escape is deliberately left alone: it already closed both, and nobody has
 * had a reason to want otherwise.
 */
const openPanels = [];

/**
 * The scroll lock belongs to the stack, not to each dialog.
 *
 * Every overlay used to save and restore `body.overflow` on its own, which
 * is correct for one and wrong for two: the inner dialog saves the outer
 * one's `hidden`, and whichever restores last wins. Closing both left the
 * console permanently unscrollable — recoverable only by reloading, and
 * indistinguishable from the page having frozen. Now the first to open
 * locks and the last to close restores.
 */
let lockedOverflow = null;

function focusableWithin(panel) {
  return Array.from(panel.querySelectorAll(FOCUSABLE));
}

/**
 * Shared overlay mechanics for the modal and the drawer. The monolith had
 * eleven separate `.modal` divs in the markup, each shown by toggling a
 * class, and none of them handled Escape, focus, or background scroll —
 * which is how a dispatcher ended up scrolling the table underneath an open
 * dialog. One implementation, four behaviors:
 *
 *  - Escape closes.
 *  - Background scroll is locked while open.
 *  - Focus moves into the dialog on open and returns to the trigger on
 *    close, so keyboard users are not dumped back at the top of the page.
 *  - Tab and Shift+Tab cycle within the dialog instead of walking out into
 *    the page behind it (Phase 9.2).
 *
 * The trap is the part that was missing, and its absence was invisible with
 * a mouse: `aria-modal` tells a screen reader the rest of the page is inert,
 * but it does not make it so. Tabbing past the last field landed on the
 * orders table underneath — still clickable, still able to open a second
 * dialog on top of this one — while the reader kept announcing the dialog.
 */
function useOverlayBehavior(open, onClose, panelRef) {
  const previouslyFocused = useRef(null);

  // The effect below must not re-run when the parent re-renders. Every call
  // site passes `onClose` as an inline arrow, so it is a new function on
  // every render of the component holding the modal — and those re-render on
  // their own, from a live update arriving over /api/stream. With `onClose`
  // in the dependency list, the cleanup ran mid-edit: focus jumped back to
  // the trigger and then to the top of the dialog, wiping out where the
  // dispatcher was typing. Reading it through a ref keeps the handler
  // current without making the setup depend on its identity.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;

    const marker = {};
    openPanels.push(marker);
    if (openPanels.length === 1) {
      lockedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      if (openPanels[openPanels.length - 1] !== marker) return;

      // Queried on every keystroke rather than cached on open: the dialogs
      // that most need this are the ones whose contents change while open —
      // a field appearing after a checkbox, a save button enabling once the
      // form is valid.
      const focusable = focusableWithin(panel);
      if (focusable.length === 0) {
        // A dialog with nothing to focus is still a dialog. Hold focus on
        // the panel rather than letting Tab escape to the page behind it.
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const inside = panel.contains(active);

      if (event.shiftKey) {
        if (!inside || active === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    // Defer so the panel exists in the DOM before we reach into it.
    const focusTimer = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const target = focusableWithin(panel)[0];
      (target ?? panel).focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(focusTimer);
      const index = openPanels.indexOf(marker);
      if (index !== -1) openPanels.splice(index, 1);
      if (openPanels.length === 0) {
        document.body.style.overflow = lockedOverflow ?? '';
        lockedOverflow = null;
      }
      previouslyFocused.current?.focus?.();
    };
  }, [open, panelRef]);
}

export function Modal({ open, onClose, title, children, footer, width = 'max-w-[520px]' }) {
  const t = useT();
  const panelRef = useRef(null);
  useOverlayBehavior(open, onClose, panelRef);
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[rgba(6,8,12,0.72)] p-4 backdrop-blur-[4px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // Focusable only programmatically: the fallback target when the
        // dialog holds nothing that can take focus on its own.
        tabIndex={-1}
        className={`panel max-h-[88vh] w-full overflow-y-auto ${width}`}
      >
        <header className="mb-3 flex items-start justify-between gap-3">
          <h2 className="font-brand text-[15px] font-bold">{title}</h2>
          <button type="button" className="btn !px-2 !py-1" onClick={onClose} aria-label={t('common.closeBtn')}>
            ✕
          </button>
        </header>
        {children}
        {footer ? <footer className="mt-4 flex flex-wrap justify-end gap-2">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

/** Same mechanics, edge-anchored. Used where the form is long enough that a
 *  centered dialog would fight the viewport on a laptop screen. */
export function Drawer({ open, onClose, title, children, footer }) {
  const t = useT();
  const panelRef = useRef(null);
  useOverlayBehavior(open, onClose, panelRef);
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex justify-end bg-[rgba(6,8,12,0.72)] backdrop-blur-[4px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="panel flex h-full w-full max-w-[440px] flex-col overflow-y-auto !rounded-none border-y-0 border-r-0"
      >
        <header className="mb-4 flex items-start justify-between gap-3">
          <h2 className="font-brand text-[15px] font-bold">{title}</h2>
          <button type="button" className="btn !px-2 !py-1" onClick={onClose} aria-label={t('common.closeBtn')}>
            ✕
          </button>
        </header>
        <div className="flex-1">{children}</div>
        {footer ? <footer className="mt-4 flex flex-wrap justify-end gap-2">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
